import React, { useState, useEffect, useCallback, useRef } from "react";
import { Map, useMap, useMapsLibrary } from "@vis.gl/react-google-maps";
import { useParams, useNavigate, Link } from "react-router-dom";
import { connect } from "react-redux";
import axios from "axios";
import styled from "styled-components";
import checkAuthz from "../../../util/authz";
import { setCreationEventContext } from "../../../util/creationEventContext";
import { setAlert } from "../../../actions/alert";
import { handleScraperError } from "../../../utils/errorHandler";
import { slugify } from "../../../util/slugify";

const PageContainer = styled.div`
  max-width: 900px;
  margin: 20px auto;
  padding: 20px;
  flex-grow: 1;
`;

const Section = styled.div`
  background: white;
  border-radius: 8px;
  padding: 24px;
  margin-bottom: 20px;
  box-shadow: 0 2px 4px rgba(0, 0, 0, 0.1);
`;

const SectionTitle = styled.h2`
  font-size: 1.3em;
  color: #2e7d32;
  margin: 0 0 16px;
`;

const FormGrid = styled.div`
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 12px;

  @media (max-width: 600px) {
    grid-template-columns: 1fr;
  }
`;

const FormGroup = styled.div`
  display: flex;
  flex-direction: column;
  gap: 4px;

  &.full-width {
    grid-column: 1 / -1;
  }
`;

const Label = styled.label`
  font-size: 0.85em;
  font-weight: 600;
  color: #555;
`;

const Input = styled.input`
  padding: 8px 12px;
  border: 1px solid #ddd;
  border-radius: 4px;
  font-size: 0.95em;

  &:focus {
    outline: none;
    border-color: #4caf50;
  }
`;

const Textarea = styled.textarea`
  padding: 8px 12px;
  border: 1px solid #ddd;
  border-radius: 4px;
  font-size: 0.95em;
  min-height: 80px;
  resize: vertical;

  &:focus {
    outline: none;
    border-color: #4caf50;
  }
`;

const Button = styled.button`
  padding: 8px 16px;
  border: none;
  border-radius: 4px;
  font-size: 0.95em;
  cursor: pointer;
  font-weight: 600;

  &:disabled {
    opacity: 0.6;
    cursor: not-allowed;
  }
`;

const PrimaryButton = styled(Button)`
  background: #4caf50;
  color: white;

  &:hover:not(:disabled) {
    background: #388e3c;
  }
`;

const SecondaryButton = styled(Button)`
  background: #f5f5f5;
  color: #333;
  border: 1px solid #ddd;

  &:hover:not(:disabled) {
    background: #e0e0e0;
  }
`;

const DangerButton = styled(Button)`
  background: none;
  color: #d32f2f;
  padding: 4px 8px;
  font-size: 0.85em;

  &:hover:not(:disabled) {
    background: #ffebee;
  }
`;

const RestaurantRow = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 10px 0;
  border-bottom: 1px solid #eee;

  &:last-child {
    border-bottom: none;
  }
`;

const RestaurantInfo = styled.div`
  flex: 1;
  min-width: 0;
`;

const RestaurantName = styled(Link)`
  font-weight: 600;
  color: #2e7d32;
  text-decoration: none;

  &:hover {
    text-decoration: underline;
  }
`;

const RestaurantAddress = styled.div`
  font-size: 0.85em;
  color: #666;
  margin-top: 2px;
`;

const RestaurantActions = styled.div`
  display: flex;
  align-items: center;
  gap: 12px;
  margin-left: 12px;
`;

const SearchContainer = styled.div`
  position: relative;
`;

const SearchInput = styled(Input)`
  width: 100%;
`;

const SearchDropdown = styled.div`
  position: absolute;
  top: 100%;
  left: 0;
  right: 0;
  background: white;
  border: 1px solid #ddd;
  border-radius: 0 0 4px 4px;
  max-height: 250px;
  overflow-y: auto;
  z-index: 10;
  box-shadow: 0 4px 8px rgba(0, 0, 0, 0.1);
`;

const SearchResultItem = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 8px 12px;
  cursor: pointer;

  &:hover {
    background: #f5f5f5;
  }
`;

const AddActions = styled.div`
  display: flex;
  gap: 12px;
  margin-top: 12px;
  flex-wrap: wrap;
`;

const CreateMethodGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(180px, 1fr));
  gap: 10px;
  margin-top: 12px;
`;

const CreateMethodCard = styled.button`
  display: flex;
  align-items: center;
  gap: 10px;
  padding: 12px 16px;
  border: 1px solid #ddd;
  border-radius: 8px;
  background: white;
  cursor: pointer;
  font-size: 0.9em;
  text-align: left;
  transition: border-color 0.2s, box-shadow 0.2s;

  &:hover {
    border-color: #4caf50;
    box-shadow: 0 1px 4px rgba(76, 175, 80, 0.15);
  }

  &:disabled {
    opacity: 0.6;
    cursor: not-allowed;
  }

  i {
    color: #4caf50;
    font-size: 1.1em;
    width: 20px;
    text-align: center;
  }
`;

const InlineForm = styled.form`
  display: flex;
  gap: 8px;
  margin-top: 8px;
`;

const HiddenFileInput = styled.input`
  display: none;
`;

const MapContainer = styled.div`
  width: 100%;
  height: 350px;
  border-radius: 8px;
  overflow: hidden;
  position: relative;
  margin-top: 8px;
  border: 1px solid #ddd;
`;

const MapCrosshair = styled.div`
  position: absolute;
  top: 50%;
  left: 50%;
  transform: translate(-50%, -50%);
  z-index: 1;
  pointer-events: none;
  font-size: 28px;
  color: #d32f2f;
  text-shadow: 0 0 4px white, 0 0 4px white;
`;

const CoordRow = styled.div`
  display: flex;
  gap: 8px;
  margin-top: 8px;
  align-items: end;
`;

const CoordField = styled.div`
  display: flex;
  flex-direction: column;
  gap: 2px;
  flex: 1;
`;

const SmallLabel = styled.label`
  font-size: 0.75em;
  color: #888;
`;

const SmallInput = styled.input`
  padding: 4px 8px;
  border: 1px solid #ddd;
  border-radius: 4px;
  font-size: 0.85em;
  font-family: monospace;

  &:focus {
    outline: none;
    border-color: #4caf50;
  }
`;

const CitySearchInput = styled.input`
  width: 100%;
  padding: 8px 12px;
  border: 1px solid #ddd;
  border-radius: 4px;
  font-size: 0.95em;

  &:focus {
    outline: none;
    border-color: #4caf50;
  }
`;

const LocationHint = styled.div`
  font-size: 0.8em;
  color: #888;
  margin-top: 4px;
`;

const ErrorMessage = styled.div`
  color: #d32f2f;
  font-size: 0.9em;
  margin-top: 8px;
`;

const SuccessMessage = styled.div`
  color: #2e7d32;
  font-size: 0.9em;
  margin-top: 8px;
`;

const formatDateForInput = (dateString) => {
  if (!dateString) return "";
  // Format as YYYY-MM-DDTHH:MM in local time for datetime-local input
  const d = new Date(dateString);
  const pad = (n) => n.toString().padStart(2, "0");
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}T${pad(d.getHours())}:${pad(d.getMinutes())}`;
};

const getAddressDisplay = (restaurant) => {
  if (restaurant.address?.formatted_address) return restaurant.address.formatted_address;
  if (restaurant.geoJson?.coordinates) {
    return `${restaurant.geoJson.coordinates[1].toFixed(4)}, ${restaurant.geoJson.coordinates[0].toFixed(4)}`;
  }
  return "";
};

const EventEditor = ({ token, setAlert }) => {
  const { slug } = useParams();
  const navigate = useNavigate();
  const isNew = slug === "new";

  const [event, setEvent] = useState(null);
  const [restaurants, setRestaurants] = useState([]);
  const [formData, setFormData] = useState({
    name: "",
    slug: "",
    lat: "",
    lng: "",
    zoom: "13",
    heading: "0",
    description: "",
    startDate: "",
    endDate: "",
  });
  const [overlayData, setOverlayData] = useState({
    imageUrl: "",
    south: "",
    west: "",
    north: "",
    east: "",
    minZoom: "15",
    opacity: "0.75",
  });
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState("");
  const [success, setSuccess] = useState("");
  const [searchQuery, setSearchQuery] = useState("");
  const [searchResults, setSearchResults] = useState([]);
  const [loadingRestaurants, setLoadingRestaurants] = useState(false);
  const [createMode, setCreateMode] = useState(null); // 'url' | 'name' | null
  const [createInput, setCreateInput] = useState("");
  const [createLoading, setCreateLoading] = useState(false);
  const [mapCenter, setMapCenter] = useState(null); // { lat, lng } tracks map drag independently
  const editorMap = useMap("event-editor-map");
  const [editorMapReady, setEditorMapReady] = useState(false);
  const placesLib = useMapsLibrary("places");
  const overlayRectRef = useRef(null);
  const overlayPreviewRef = useRef(null);
  const fileInputRef = useRef(null);
  const pdfInputRef = useRef(null);
  const cityInputRef = useRef(null);
  const autocompleteRef = useRef(null);

  const isAdmin = checkAuthz(token, ["admin"]);

  useEffect(function redirectNonAdminUsers() {
    if (!isAdmin) {
      navigate("/");
    }
  }, [isAdmin, navigate]);

  // Fetch event data for existing events
  useEffect(function fetchEventDataOnLoad() {
    if (isNew || !token) return;

    const fetchEvent = async () => {
      try {
        const res = await axios.get(`/api/events/${slug}`);
        const evt = res.data;
        setEvent(evt);
        setFormData({
          name: evt.name || "",
          slug: evt.slug || "",
          lat: evt.lat?.toString() || "",
          lng: evt.lng?.toString() || "",
          zoom: evt.zoom?.toString() || "13",
          heading: evt.heading?.toString() || "0",
          description: evt.description || "",
          startDate: formatDateForInput(evt.startDate),
          endDate: formatDateForInput(evt.endDate),
        });
        if (evt.mapOverlay) {
          setOverlayData({
            imageUrl: evt.mapOverlay.imageUrl || "",
            south: evt.mapOverlay.bounds?.south?.toString() || "",
            west: evt.mapOverlay.bounds?.west?.toString() || "",
            north: evt.mapOverlay.bounds?.north?.toString() || "",
            east: evt.mapOverlay.bounds?.east?.toString() || "",
            minZoom: evt.mapOverlay.minZoom?.toString() || "15",
            opacity: evt.mapOverlay.opacity?.toString() || "0.75",
          });
        }
        if (evt.lat && evt.lng) {
          setMapCenter({ lat: evt.lat, lng: evt.lng });
        }
      } catch (err) {
        setError("Failed to load event");
      }
    };

    fetchEvent();
  }, [slug, isNew, token]);

  // Fetch restaurants for existing events
  const fetchRestaurants = useCallback(async () => {
    if (!event?._id) return;
    setLoadingRestaurants(true);
    try {
      const res = await axios.get(`/api/restaurants/query?eventId=${event._id}`);
      setRestaurants(res.data);
    } catch (err) {
      console.error("Error fetching event restaurants:", err);
    } finally {
      setLoadingRestaurants(false);
    }
  }, [event?._id]);

  useEffect(function fetchRestaurantsWhenEventChanges() {
    fetchRestaurants();
  }, [fetchRestaurants]);

  // Debounced search
  useEffect(function debouncedRestaurantSearch() {
    if (searchQuery.length < 2) {
      setSearchResults([]);
      return;
    }

    const timer = setTimeout(async () => {
      try {
        const res = await axios.get(
          `/api/restaurants/search?name=${encodeURIComponent(searchQuery)}&limit=10`
        );
        // Filter out restaurants already in the event
        const existingIds = new Set(restaurants.map((r) => r._id));
        setSearchResults(res.data.filter((r) => !existingIds.has(r._id)));
      } catch (err) {
        console.error("Search error:", err);
      }
    }, 300);

    return () => clearTimeout(timer);
  }, [searchQuery, restaurants]);

  // Initialize Google Places Autocomplete for city search
  useEffect(function initGooglePlacesAutocomplete() {
    if (!placesLib || !cityInputRef.current || autocompleteRef.current) return;

    const autocomplete = new placesLib.Autocomplete(
      cityInputRef.current,
      { types: ["(cities)"] }
    );

    autocomplete.addListener("place_changed", () => {
      const place = autocomplete.getPlace();
      if (!place.geometry) return;
      const lat = place.geometry.location.lat().toString();
      const lng = place.geometry.location.lng().toString();
      setFormData((prev) => ({ ...prev, lat, lng }));
      setMapCenter({ lat: parseFloat(lat), lng: parseFloat(lng) });
    });

    autocompleteRef.current = autocomplete;
  }, [placesLib]);

  // Create or destroy the editable rectangle when bounds appear or are cleared.
  // The rectangle is the source of truth for bounds once created — its
  // bounds_changed listener pushes updates to overlayData, not the other way.
  // Track editor map readiness
  useEffect(function trackEditorMapReady() {
    if (!editorMap) { setEditorMapReady(false); return; }
    const listener = editorMap.addListener("idle", () => setEditorMapReady(true));
    return () => listener.remove();
  }, [editorMap]);

  // Pan to mapCenter when it changes (city search, manual coord input)
  const prevEditorCenterRef = useRef(null);
  useEffect(function panEditorMap() {
    if (!editorMap || !mapCenter) return;
    const prev = prevEditorCenterRef.current;
    if (prev && prev.lat === mapCenter.lat && prev.lng === mapCenter.lng) return;
    prevEditorCenterRef.current = mapCenter;
    editorMap.panTo(mapCenter);
  }, [editorMap, mapCenter]);

  useEffect(function manageOverlayRect() {
    if (!editorMap || !window.google?.maps || !editorMapReady) return;

    const hasBounds =
      overlayData.south && overlayData.west && overlayData.north && overlayData.east;

    if (!hasBounds) {
      if (overlayRectRef.current) {
        try { overlayRectRef.current.setMap(null); } catch (e) {}
        overlayRectRef.current = null;
      }
      return;
    }

    // Only create the rectangle if one doesn't already exist
    if (overlayRectRef.current) return;

    try {
      const bounds = new window.google.maps.LatLngBounds(
        new window.google.maps.LatLng(parseFloat(overlayData.south), parseFloat(overlayData.west)),
        new window.google.maps.LatLng(parseFloat(overlayData.north), parseFloat(overlayData.east))
      );

      const rect = new window.google.maps.Rectangle({
        bounds,
        editable: true,
        draggable: true,
        strokeColor: "#4CAF50",
        strokeWeight: 2,
        fillColor: "#4CAF50",
        fillOpacity: 0.05,
        map: editorMap,
      });

      rect.addListener("bounds_changed", () => {
        try {
          const b = rect.getBounds();
          const sw = b.getSouthWest();
          const ne = b.getNorthEast();
          setOverlayData((prev) => ({
            ...prev,
            south: sw.lat().toFixed(6),
            west: sw.lng().toFixed(6),
            north: ne.lat().toFixed(6),
            east: ne.lng().toFixed(6),
          }));
        } catch (e) {
          // Map may have been destroyed during re-render
        }
      });

      overlayRectRef.current = rect;
    } catch (e) {
      console.debug("Could not create overlay rectangle:", e.message);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [editorMap, editorMapReady, !!overlayData.south]);

  // Update the ground overlay image preview when bounds, imageUrl, or opacity change.
  // Debounced to avoid destroying/recreating during rapid rectangle drags
  // (which would prevent the image from ever finishing loading).
  const previewTimerRef = useRef(null);
  useEffect(function manageOverlayPreview() {
    if (!editorMap || !window.google?.maps || !editorMapReady) return;

    clearTimeout(previewTimerRef.current);
    previewTimerRef.current = setTimeout(() => {
      // Clean up previous preview
      if (overlayPreviewRef.current) {
        try { overlayPreviewRef.current.setMap(null); } catch (e) {}
        overlayPreviewRef.current = null;
      }

      const hasBounds =
        overlayData.south && overlayData.west && overlayData.north && overlayData.east;
      if (!hasBounds || !overlayData.imageUrl) return;

      try {
        const bounds = new window.google.maps.LatLngBounds(
          new window.google.maps.LatLng(parseFloat(overlayData.south), parseFloat(overlayData.west)),
          new window.google.maps.LatLng(parseFloat(overlayData.north), parseFloat(overlayData.east))
        );

        overlayPreviewRef.current = new window.google.maps.GroundOverlay(
          overlayData.imageUrl,
          bounds,
          {
            opacity: parseFloat(overlayData.opacity) || 0.75,
            clickable: false,
            map: editorMap,
          }
        );
      } catch (e) {
        console.debug("Could not create overlay preview:", e.message);
      }
    }, 300);

    return () => clearTimeout(previewTimerRef.current);
  }, [editorMap, editorMapReady, overlayData]);

  const initOverlayBounds = useCallback(() => {
    if (!editorMap) return;
    let b;
    try { b = editorMap.getBounds(); } catch (e) { return; }
    if (!b) return;
    const sw = b.getSouthWest();
    const ne = b.getNorthEast();
    // Inset to ~60% of viewport so drag handles are accessible
    const latPad = (ne.lat() - sw.lat()) * 0.2;
    const lngPad = (ne.lng() - sw.lng()) * 0.2;
    setOverlayData((prev) => ({
      ...prev,
      south: (sw.lat() + latPad).toFixed(6),
      west: (sw.lng() + lngPad).toFixed(6),
      north: (ne.lat() - latPad).toFixed(6),
      east: (ne.lng() - lngPad).toFixed(6),
    }));
  }, [editorMap]);

  const clearOverlayBounds = useCallback(() => {
    if (overlayRectRef.current) {
      overlayRectRef.current.setMap(null);
      overlayRectRef.current = null;
    }
    if (overlayPreviewRef.current) {
      overlayPreviewRef.current.setMap(null);
      overlayPreviewRef.current = null;
    }
    setOverlayData((prev) => ({
      ...prev,
      imageUrl: "",
      south: "",
      west: "",
      north: "",
      east: "",
    }));
  }, []);

  const handleMapChange = ({ center, zoom }) => {
    setFormData((prev) => ({
      ...prev,
      lat: center.lat.toFixed(6),
      lng: center.lng.toFixed(6),
      zoom: zoom.toString(),
    }));
  };

  const handleChange = (e) => {
    setFormData({ ...formData, [e.target.name]: e.target.value });
    setError("");
    setSuccess("");
  };

  // When lat/lng fields are edited manually, sync the map center
  const handleCoordChange = (e) => {
    handleChange(e);
    const { name, value } = e.target;
    const num = parseFloat(value);
    if (!isNaN(num)) {
      setMapCenter((prev) => ({
        lat: name === "lat" ? num : (prev?.lat ?? (parseFloat(formData.lat) || 0)),
        lng: name === "lng" ? num : (prev?.lng ?? (parseFloat(formData.lng) || 0)),
      }));
    }
  };

  const handleSave = async (e) => {
    e.preventDefault();
    setSaving(true);
    setError("");
    setSuccess("");

    try {
      // Build mapOverlay if an image URL is provided
      const mapOverlay = overlayData.imageUrl
        ? {
            imageUrl: overlayData.imageUrl,
            bounds: {
              south: parseFloat(overlayData.south) || undefined,
              west: parseFloat(overlayData.west) || undefined,
              north: parseFloat(overlayData.north) || undefined,
              east: parseFloat(overlayData.east) || undefined,
            },
            minZoom: parseInt(overlayData.minZoom) || 15,
            opacity: parseFloat(overlayData.opacity) ?? 0.75,
          }
        : null;

      const payload = { ...formData, mapOverlay };

      if (isNew) {
        const res = await axios.post("/api/events", payload);
        setSuccess("Event created");
        navigate(`/admin/events/${res.data.slug}`, { replace: true });
      } else {
        const res = await axios.put(`/api/events/${event._id}`, payload);
        setEvent(res.data);
        setSuccess("Event saved");
        // If slug changed, navigate to new URL
        if (res.data.slug !== slug) {
          navigate(`/admin/events/${res.data.slug}`, { replace: true });
        }
      }
    } catch (err) {
      const msg = err.response?.data?.errors?.[0]?.msg || "Failed to save event";
      setError(msg);
    } finally {
      setSaving(false);
    }
  };

  const handleToggleEventOnly = async (restaurant) => {
    try {
      await axios.put(`/api/restaurants/${restaurant._id}`, {
        eventOnly: !restaurant.eventOnly,
      });
      setRestaurants((prev) =>
        prev.map((r) =>
          r._id === restaurant._id ? { ...r, eventOnly: !r.eventOnly } : r
        )
      );
    } catch (err) {
      console.error("Toggle error:", err);
    }
  };

  const handleRemoveRestaurant = async (restaurantId) => {
    try {
      await axios.put(`/api/restaurants/${restaurantId}/remove-event`);
      setRestaurants((prev) => prev.filter((r) => r._id !== restaurantId));
    } catch (err) {
      console.error("Remove error:", err);
    }
  };

  const handleAddExisting = async (restaurant) => {
    try {
      await axios.put("/api/restaurants/bulk-assign-event", {
        restaurantIds: [restaurant._id],
        eventId: event._id,
      });
      setSearchQuery("");
      setSearchResults([]);
      fetchRestaurants();
    } catch (err) {
      console.error("Add error:", err);
    }
  };

  const setEventContext = () => {
    setCreationEventContext(event._id, `/admin/events/${event.slug}`);
  };

  const handleUrlSubmit = async (e) => {
    e.preventDefault();
    if (!createInput.trim()) return;
    setCreateLoading(true);
    setEventContext();
    try {
      const url = createInput.trim().replace(/(^\w+:|^)\/\//, "");
      const response = await axios.post("/api/restaurant_builder/process_url_async", { url });
      navigate(`/create/processing/${response.data.jobId}`);
    } catch (err) {
      setCreateLoading(false);
      handleScraperError(err, setAlert);
    }
    setCreateInput("");
    setCreateMode(null);
  };

  const handleNameSubmit = (e) => {
    e.preventDefault();
    if (!createInput.trim()) return;
    setEventContext();
    navigate("/create/v2/address", { state: { name: createInput.trim() } });
    setCreateInput("");
    setCreateMode(null);
  };

  const handlePdfUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (file.size > 20 * 1024 * 1024) {
      setAlert("File size exceeds 20MB limit.", "failure");
      e.target.value = "";
      return;
    }
    setCreateLoading(true);
    setEventContext();
    try {
      const postData = new FormData();
      postData.append("pdf", file);
      const response = await axios.post("/api/restaurant_builder/upload_pdf_async", postData, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      navigate(`/create/processing/${response.data.jobId}`);
    } catch (err) {
      setCreateLoading(false);
      handleScraperError(err, setAlert);
    }
    if (pdfInputRef.current) pdfInputRef.current.value = "";
  };

  const handleImageUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (file.size > 10 * 1024 * 1024) {
      setAlert("File size exceeds 10MB limit.", "failure");
      e.target.value = "";
      return;
    }
    setCreateLoading(true);
    setEventContext();
    try {
      const postData = new FormData();
      postData.append("image", file);
      const response = await axios.post("/api/restaurant_builder/upload_async", postData, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      navigate(`/create/processing/${response.data.jobId}`);
    } catch (err) {
      setCreateLoading(false);
      handleScraperError(err, setAlert);
    }
    if (fileInputRef.current) fileInputRef.current.value = "";
  };

  if (!isAdmin) return null;

  return (
    <PageContainer>
      <div style={{ marginBottom: 16 }}>
        <Link to="/events" style={{ color: "#2e7d32", textDecoration: "none" }}>
          &larr; Back to Events
        </Link>
      </div>

      <Section>
        <SectionTitle>{isNew ? "Create Event" : "Edit Event"}</SectionTitle>
        <form onSubmit={handleSave}>
          <FormGrid>
            <FormGroup>
              <Label>Name</Label>
              <Input
                name="name"
                value={formData.name}
                onChange={handleChange}
                required
              />
            </FormGroup>
            <FormGroup>
              <Label>Slug</Label>
              <Input
                name="slug"
                value={formData.slug}
                onChange={handleChange}
                required
                pattern="[a-z0-9-]+"
                title="Lowercase letters, numbers, and hyphens only"
              />
            </FormGroup>
            <FormGroup className="full-width">
              <Label>Location</Label>
              <CitySearchInput
                ref={cityInputRef}
                type="text"
                placeholder="Search for a city..."
              />
              <LocationHint>
                Search a city above, then drag the map to fine-tune the centroid.
              </LocationHint>
              {mapCenter ? (
                <MapContainer>
                  <Map
                    id="event-editor-map"
                    defaultCenter={mapCenter}
                    defaultZoom={parseInt(formData.zoom) || 13}
                    gestureHandling="greedy"
                    mapTypeControl={false}
                    streetViewControl={false}
                    mapId="ae90486c215209d65677867a"
                    heading={parseInt(formData.heading) || 0}
                    onIdle={() => {
                      if (!editorMap) return;
                      const center = editorMap.getCenter();
                      const zoom = editorMap.getZoom();
                      if (!center) return;
                      handleMapChange({ center: { lat: center.lat(), lng: center.lng() }, zoom });
                    }}
                  />
                  <MapCrosshair>+</MapCrosshair>
                </MapContainer>
              ) : (
                <MapContainer style={{ display: "flex", alignItems: "center", justifyContent: "center", background: "#f0f0f0" }}>
                  <span style={{ color: "#999" }}>Search a city to show the map</span>
                </MapContainer>
              )}
              <CoordRow>
                <CoordField>
                  <SmallLabel>Lat</SmallLabel>
                  <SmallInput
                    name="lat"
                    type="number"
                    step="any"
                    value={formData.lat}
                    onChange={handleCoordChange}
                    required
                  />
                </CoordField>
                <CoordField>
                  <SmallLabel>Lng</SmallLabel>
                  <SmallInput
                    name="lng"
                    type="number"
                    step="any"
                    value={formData.lng}
                    onChange={handleCoordChange}
                    required
                  />
                </CoordField>
                <CoordField style={{ maxWidth: 80 }}>
                  <SmallLabel>Zoom</SmallLabel>
                  <SmallInput
                    name="zoom"
                    type="number"
                    min="1"
                    max="20"
                    value={formData.zoom}
                    onChange={handleChange}
                  />
                </CoordField>
              </CoordRow>
              <div style={{ marginTop: 12 }}>
                <SmallLabel>Map Heading ({formData.heading || 0}&deg;)</SmallLabel>
                <input
                  type="range"
                  min="0"
                  max="360"
                  step="5"
                  value={formData.heading || 0}
                  onChange={(e) =>
                    setFormData((prev) => ({ ...prev, heading: e.target.value }))
                  }
                  style={{ width: "100%" }}
                />
              </div>
            </FormGroup>
            <FormGroup>
              <Label>Start Date & Time</Label>
              <Input
                name="startDate"
                type="datetime-local"
                value={formData.startDate}
                onChange={handleChange}
              />
            </FormGroup>
            <FormGroup>
              <Label>End Date & Time</Label>
              <Input
                name="endDate"
                type="datetime-local"
                value={formData.endDate}
                onChange={handleChange}
              />
            </FormGroup>
            <FormGroup className="full-width">
              <Label>Description</Label>
              <Textarea
                name="description"
                value={formData.description}
                onChange={handleChange}
              />
            </FormGroup>
          </FormGrid>

          {error && <ErrorMessage>{error}</ErrorMessage>}
          {success && <SuccessMessage>{success}</SuccessMessage>}

          <div style={{ marginTop: 16 }}>
            <PrimaryButton type="submit" disabled={saving}>
              {saving ? "Saving..." : isNew ? "Create Event" : "Save Changes"}
            </PrimaryButton>
          </div>
        </form>
      </Section>

      <Section>
        <SectionTitle>Map Overlay</SectionTitle>
        <FormGrid>
          <FormGroup className="full-width">
            <Label>Image URL</Label>
            <Input
              placeholder="https://example.com/venue-map.png"
              value={overlayData.imageUrl}
              onChange={(e) =>
                setOverlayData({ ...overlayData, imageUrl: e.target.value })
              }
            />
            <LocationHint>
              PNG with transparency recommended. The image will be stretched to
              fit the bounds below.
            </LocationHint>
          </FormGroup>
          <FormGroup className="full-width">
            <Label>Bounds</Label>
            {overlayData.south ? (
              <>
                <LocationHint>
                  Drag or resize the green rectangle on the map above to adjust
                  bounds. The overlay image is previewed inside the rectangle.
                </LocationHint>
                <CoordRow>
                  <CoordField>
                    <SmallLabel>South</SmallLabel>
                    <SmallInput type="text" value={overlayData.south} readOnly />
                  </CoordField>
                  <CoordField>
                    <SmallLabel>West</SmallLabel>
                    <SmallInput type="text" value={overlayData.west} readOnly />
                  </CoordField>
                  <CoordField>
                    <SmallLabel>North</SmallLabel>
                    <SmallInput type="text" value={overlayData.north} readOnly />
                  </CoordField>
                  <CoordField>
                    <SmallLabel>East</SmallLabel>
                    <SmallInput type="text" value={overlayData.east} readOnly />
                  </CoordField>
                </CoordRow>
                <div style={{ marginTop: 8 }}>
                  <DangerButton type="button" onClick={clearOverlayBounds}>
                    Clear Overlay
                  </DangerButton>
                </div>
              </>
            ) : (
              <>
                <LocationHint>
                  Position the map above to your event location, then click the
                  button below. A draggable rectangle will appear on the map —
                  resize it to match your venue.
                </LocationHint>
                <div style={{ marginTop: 8 }}>
                  <SecondaryButton
                    type="button"
                    onClick={initOverlayBounds}
                    disabled={!editorMap}
                  >
                    Set Bounds from Current View
                  </SecondaryButton>
                </div>
              </>
            )}
          </FormGroup>
          <FormGroup>
            <Label>Min Zoom (show overlay at this zoom+)</Label>
            <SmallInput
              type="number"
              min="1"
              max="20"
              value={overlayData.minZoom}
              onChange={(e) =>
                setOverlayData({ ...overlayData, minZoom: e.target.value })
              }
            />
          </FormGroup>
          <FormGroup>
            <Label>Opacity ({overlayData.opacity})</Label>
            <input
              type="range"
              min="0"
              max="1"
              step="0.05"
              value={overlayData.opacity}
              onChange={(e) =>
                setOverlayData({ ...overlayData, opacity: e.target.value })
              }
              style={{ width: "100%" }}
            />
          </FormGroup>
        </FormGrid>
      </Section>

      {!isNew && event && (
        <Section>
          <SectionTitle>
            Restaurants ({restaurants.length})
          </SectionTitle>

          {loadingRestaurants ? (
            <div>Loading restaurants...</div>
          ) : restaurants.length === 0 ? (
            <div style={{ color: "#666", marginBottom: 16 }}>
              No restaurants linked to this event yet.
            </div>
          ) : (
            <div>
              {restaurants.map((r) => (
                <RestaurantRow key={r._id}>
                  <RestaurantInfo>
                    <RestaurantName to={`/details/${r._id}/${slugify(r.name)}`}>
                      {r.name}
                    </RestaurantName>
                    <RestaurantAddress>
                      {getAddressDisplay(r)}
                    </RestaurantAddress>
                  </RestaurantInfo>
                  <RestaurantActions>
                    <label
                      style={{ fontSize: "0.85em", display: "flex", alignItems: "center", gap: 4 }}
                    >
                      <input
                        type="checkbox"
                        checked={!!r.eventOnly}
                        onChange={() => handleToggleEventOnly(r)}
                      />
                      Event-only
                    </label>
                    <DangerButton onClick={() => handleRemoveRestaurant(r._id)}>
                      Remove
                    </DangerButton>
                  </RestaurantActions>
                </RestaurantRow>
              ))}
            </div>
          )}

          <div style={{ marginTop: 20 }}>
            <Label>Create New Restaurant</Label>
            <CreateMethodGrid>
              <CreateMethodCard
                type="button"
                disabled={createLoading}
                onClick={() => { setCreateMode(createMode === "url" ? null : "url"); setCreateInput(""); }}
              >
                <i className="fas fa-link" />
                <span>From URL</span>
              </CreateMethodCard>
              <CreateMethodCard
                type="button"
                disabled={createLoading}
                onClick={() => { setCreateMode(createMode === "name" ? null : "name"); setCreateInput(""); }}
              >
                <i className="fas fa-pencil-alt" />
                <span>Enter Name</span>
              </CreateMethodCard>
              <CreateMethodCard
                type="button"
                disabled={createLoading}
                onClick={() => pdfInputRef.current?.click()}
              >
                <i className="fas fa-file-pdf" />
                <span>Upload PDF</span>
              </CreateMethodCard>
              <CreateMethodCard
                type="button"
                disabled={createLoading}
                onClick={() => fileInputRef.current?.click()}
              >
                <i className="fas fa-camera" />
                <span>Upload Image</span>
              </CreateMethodCard>
            </CreateMethodGrid>

            {createMode === "url" && (
              <InlineForm onSubmit={handleUrlSubmit}>
                <Input
                  style={{ flex: 1 }}
                  placeholder="Restaurant website URL..."
                  value={createInput}
                  onChange={(e) => setCreateInput(e.target.value)}
                  autoFocus
                  disabled={createLoading}
                />
                <PrimaryButton type="submit" disabled={createLoading || !createInput.trim()}>
                  {createLoading ? "Processing..." : "Go"}
                </PrimaryButton>
              </InlineForm>
            )}

            {createMode === "name" && (
              <InlineForm onSubmit={handleNameSubmit}>
                <Input
                  style={{ flex: 1 }}
                  placeholder="Restaurant name..."
                  value={createInput}
                  onChange={(e) => setCreateInput(e.target.value)}
                  autoFocus
                />
                <PrimaryButton type="submit" disabled={!createInput.trim()}>
                  Go
                </PrimaryButton>
              </InlineForm>
            )}

            <HiddenFileInput
              ref={pdfInputRef}
              type="file"
              accept="application/pdf"
              onChange={handlePdfUpload}
            />
            <HiddenFileInput
              ref={fileInputRef}
              type="file"
              accept="image/*"
              onChange={handleImageUpload}
            />
          </div>

          <div style={{ marginTop: 16 }}>
            <Label>Add Existing Restaurant</Label>
            <SearchContainer>
              <SearchInput
                placeholder="Search by name..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
              />
              {searchResults.length > 0 && (
                <SearchDropdown>
                  {searchResults.map((r) => (
                    <SearchResultItem key={r._id}>
                      <div>
                        <div style={{ fontWeight: 600 }}>{r.name}</div>
                        <div style={{ fontSize: "0.8em", color: "#666" }}>
                          {getAddressDisplay(r)}
                        </div>
                      </div>
                      <SecondaryButton
                        type="button"
                        onClick={() => handleAddExisting(r)}
                        style={{ marginLeft: 8 }}
                      >
                        Add
                      </SecondaryButton>
                    </SearchResultItem>
                  ))}
                </SearchDropdown>
              )}
            </SearchContainer>
          </div>
        </Section>
      )}
    </PageContainer>
  );
};

const mapStateToProps = (state) => ({
  token: state.auth.token,
});

export default connect(mapStateToProps, { setAlert })(EventEditor);
